import type { TFunction } from "i18next";

import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import type { ReviewActivityItem } from "../../types/review";

/* ──────────────────────────────────────────────────────────────────
 * A "session" = same reviewer + same timestamp (±2 sec).
 * Batch operations produce identical timestamps, so this collapses
 * "Darja corrected 15 images" into one expandable row.
 * ────────────────────────────────────────────────────────────────── */

export type ActionKind = "corrected" | "added" | "removed" | "undo";

export interface ActivityChip {
  kind: ActionKind;
  family_key: string;
  model_label: string | null;
  human_label: string | null;
}

export interface SessionImage {
  image_id: number;
  signed_url: string;
  machine_name: string;
}

export type SessionBucket = "now" | "today" | "earlier";

export interface ReviewSession {
  id: string;
  reviewer_name: string;
  reviewed_at: string;
  images: SessionImage[];
  chips: ActivityChip[];
  markedInvalid: boolean;
  /** Time bucket for section headers */
  bucket: SessionBucket;
}

/**
 * Infer the review action from label nullability. Temporary until the
 * backend returns an explicit `action` field — then read it directly.
 *
 *   model + human   corrected (prediction was corrected)
 *   human only      added     (label added manually)
 *   model only      undo      (correction reverted to the prediction)
 *   neither         removed   (label removed)
 */
export const inferAction = (item: ReviewActivityItem): ActionKind => {
  if (item.model_label && item.human_label) return "corrected";
  if (!item.model_label && item.human_label) return "added";
  if (item.model_label && !item.human_label) return "undo";
  return "removed";
};

/** Summary phrasing per action kind. Kinds are internal values, not wire ones. */
type SummaryKey =
  | "recent.summary.corrected"
  | "recent.summary.added"
  | "recent.summary.removed"
  | "recent.summary.undo";

const KIND_SUMMARY_KEYS: Record<ActionKind, SummaryKey> = {
  corrected: "recent.summary.corrected",
  added: "recent.summary.added",
  removed: "recent.summary.removed",
  undo: "recent.summary.undo",
};

const SUMMARY_ORDER: ActionKind[] = ["corrected", "added", "removed", "undo"];

/**
 * "corrected 5 images, added 2 labels" — counts chips by kind, joins
 * non-zero parts with commas. Null when the session has no chips.
 */
export const summarizeChips = (
  chips: ActivityChip[],
  t: TFunction<"mlExports">,
): string | null => {
  if (chips.length === 0) return null;
  const counts = new Map<ActionKind, number>();
  for (const chip of chips) {
    counts.set(chip.kind, (counts.get(chip.kind) ?? 0) + 1);
  }
  const parts = SUMMARY_ORDER.filter((kind) => counts.has(kind)).map((kind) => {
    const n = counts.get(kind)!;
    return t(KIND_SUMMARY_KEYS[kind], { count: n, value: formatNumber(n) });
  });
  return parts.join(", ");
};

const TIMESTAMP_TOLERANCE_MS = 2000;

export const getTimeBucket = (isoDate: string): SessionBucket => {
  const date = new Date(isoDate);
  const now = Date.now();
  const diffMs = now - date.getTime();
  if (diffMs < 5 * 60 * 1000) return "now";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date.getTime() >= today.getTime()) return "today";
  return "earlier";
};

export const BUCKET_LABEL_KEYS: Record<
  string,
  "recent.bucket.now" | "recent.bucket.today" | "recent.bucket.earlier"
> = {
  now: "recent.bucket.now",
  today: "recent.bucket.today",
  earlier: "recent.bucket.earlier",
};

export const buildSessions = (
  activity: ReviewActivityItem[],
  t: TFunction<"mlExports">,
): ReviewSession[] => {
  const sessions: ReviewSession[] = [];
  const imagesSeen = new Map<string, Set<number>>(); // sessionId → image_ids

  for (const item of activity) {
    const reviewerName =
      item.reviewer?.full_name ||
      item.reviewer?.username ||
      t("recent.unknownReviewer");
    const reviewerKey = `${item.reviewer?.id ?? "unknown"}`;
    const itemTime = new Date(item.reviewed_at).getTime();

    // Find existing session for this reviewer within tolerance
    let session = sessions.find((s) => {
      if (!s.id.startsWith(reviewerKey + ":")) return false;
      const sTime = new Date(s.reviewed_at).getTime();
      return Math.abs(sTime - itemTime) <= TIMESTAMP_TOLERANCE_MS;
    });

    if (!session) {
      const id = `${reviewerKey}:${item.reviewed_at}`;
      session = {
        id,
        reviewer_name: reviewerName,
        reviewed_at: item.reviewed_at,
        images: [],
        chips: [],
        markedInvalid: false,
        bucket: getTimeBucket(item.reviewed_at),
      };
      sessions.push(session);
      imagesSeen.set(id, new Set());
    }

    // Add image (deduplicate)
    const seen = imagesSeen.get(session.id)!;
    if (!seen.has(item.image_id)) {
      seen.add(item.image_id);
      session.images.push({
        image_id: item.image_id,
        signed_url: item.signed_url,
        machine_name: item.machine_name,
      });
    }

    // Add an activity chip for any item with at least one label.
    // Both-null items are skipped: until the backend sends an explicit
    // `action` field we can't tell a real "removed" from an unrelated
    // empty activity item (inferAction still maps both-null → removed
    // for when that field lands).
    if (item.family_key && (item.model_label || item.human_label)) {
      const kind = inferAction(item);
      const alreadyHas = session.chips.some(
        (c) =>
          c.kind === kind &&
          c.family_key === item.family_key &&
          c.model_label === item.model_label &&
          c.human_label === item.human_label,
      );
      if (!alreadyHas) {
        session.chips.push({
          kind,
          family_key: item.family_key,
          model_label: item.model_label,
          human_label: item.human_label,
        });
      }
    }

    if (item.dump_image_valid === false) {
      session.markedInvalid = true;
    }
  }

  return sessions;
};
